import { extractApiErrorMessage } from './extract-api-error-message'
import {
  extractStatus,
  extractMedia,
  pickFirstNonEmpty,
  findMidjourneyGenerations,
  toDataMediaUrl
} from './aim-unified-poll-common'

function fetchBlobUrl(url) {
  var u = String(url || '').trim()
  if (!u) return Promise.resolve(null)
  return fetch(u, { mode: 'cors', credentials: 'omit' })
    .then(function (res) {
      return res && res.ok ? res.blob() : null
    })
    .catch(function () {
      return null
    })
}

function fetchMusicAssetsThenFinish(tracks, finishWithParsed) {
  var audioTasks = tracks.map(function (t) {
    return fetchBlobUrl(t.audio_url)
  })
  var coverTasks = tracks.map(function (t) {
    var cu = t.image_large_url || t.image_url || ''
    return fetchBlobUrl(cu)
  })
  return Promise.all(audioTasks).then(function (audioBlobs) {
    return Promise.all(coverTasks).then(function (coverBlobs) {
      finishWithParsed({
        status: 'completed',
        tracks: tracks,
        audioBlobs: audioBlobs,
        coverBlobs: coverBlobs
      })
    })
  })
}

/**
 * generations[]：以条内 status 为完成态（含 succeed）且带音频 url；processing 等继续轮询。
 */
function parseMusicGenerationsUnified(stateData) {
  var gens = findMidjourneyGenerations(stateData)
  if (!gens || !gens.length) return null

  var tracks = []
  var waiting = false

  for (var i = 0; i < gens.length; i++) {
    var g = gens[i]
    if (!g || typeof g !== 'object') continue
    var st = String(g.status || '').trim().toLowerCase()
    var au = pickFirstNonEmpty(g, ['url', 'audio_url', 'audioUrl', 'download_url', 'downloadUrl'])
    var img = pickFirstNonEmpty(g, ['imageUrl', 'image_url', 'image_large_url', 'imageLargeUrl'])

    if (st === 'failed' || st === 'failure' || st === 'error' || st === 'canceled' || st === 'cancelled') {
      return {
        kind: 'failed',
        error: pickFirstNonEmpty(g, ['failReason', 'error', 'message']) || '生成失败'
      }
    }

    var isWait =
      st === 'processing' ||
      st === 'pending' ||
      st === 'waiting' ||
      st === 'in_progress' ||
      st === 'queued' ||
      st === 'starting' ||
      st === ''

    var isDone =
      st === 'completed' ||
      st === 'complete' ||
      st === 'success' ||
      st === 'succeeded' ||
      st === 'succeed' ||
      st === 'done'

    if (isWait || (isDone && !au)) {
      waiting = true
      continue
    }

    if (isDone && au) {
      tracks.push({
        audio_url: au,
        image_large_url: img || '',
        image_url: img || '',
        title: g.title != null ? String(g.title) : '',
        prompt: g.prompt != null ? String(g.prompt) : '',
        tags: g.tags != null ? String(g.tags) : '',
        duration: Number(g.duration) || 0
      })
    }
  }

  if (waiting) return { kind: 'wait' }
  if (tracks.length === gens.length && tracks.length > 0) return { kind: 'done', tracks: tracks }
  return null
}

function collectMusicTrackCandidates(stateData) {
  var rows = []
  if (!stateData || typeof stateData !== 'object') return rows
  function pushGens(gens) {
    if (!Array.isArray(gens)) return
    for (var i = 0; i < gens.length; i++) {
      var g = gens[i]
      if (g && typeof g === 'object') rows.push(g)
    }
  }
  pushGens(stateData.generations)
  if (stateData.data && typeof stateData.data === 'object') {
    pushGens(stateData.data.generations)
    if (stateData.data.data && typeof stateData.data.data === 'object') {
      pushGens(stateData.data.data.generations)
    }
  }
  var tlist = stateData.tracks
  if (!tlist && stateData.data && stateData.data.tracks) tlist = stateData.data.tracks
  if (Array.isArray(tlist)) {
    for (var j = 0; j < tlist.length; j++) {
      if (tlist[j] && typeof tlist[j] === 'object') rows.push(tlist[j])
    }
  }
  return rows
}

function rowsToMusicTracks(rows) {
  var out = []
  for (var i = 0; i < rows.length; i++) {
    var r = rows[i]
    var au = pickFirstNonEmpty(r, ['audio_url', 'audioUrl', 'url', 'download_url', 'downloadUrl'])
    if (!au) continue
    out.push({
      audio_url: au,
      image_large_url: pickFirstNonEmpty(r, ['image_large_url', 'imageLargeUrl', 'image_url', 'imageUrl']) || '',
      image_url: pickFirstNonEmpty(r, ['image_url', 'imageUrl']) || '',
      title: r.title != null ? String(r.title) : '',
      prompt: r.prompt != null ? String(r.prompt) : (r.lyrics != null ? String(r.lyrics) : ''),
      tags: r.tags != null ? String(r.tags) : (r.style != null ? String(r.style) : ''),
      duration: Number(r.duration) || 0
    })
  }
  return out
}

/**
 * /aim/call 统一轮询 — music：model_type=music
 */
export function runWanUnifiedPollMusicBranch(args) {
  var stateData = args.stateData
  var modelType = args.modelType
  var finishWithParsed = args.finishWithParsed
  var scheduleNext = args.scheduleNext
  var callback = args.callback

  var unified = parseMusicGenerationsUnified(stateData)
  if (unified) {
    if (unified.kind === 'wait') {
      if (typeof callback === 'function') callback(null, { status: 'processing' })
      scheduleNext()
      return
    }
    if (unified.kind === 'failed') {
      finishWithParsed({ status: 'failed', error: unified.error || '生成失败' })
      return
    }
    if (unified.kind === 'done' && unified.tracks && unified.tracks.length) {
      fetchMusicAssetsThenFinish(unified.tracks, finishWithParsed).catch(function () {
        finishWithParsed({ status: 'completed', tracks: unified.tracks })
      })
      return
    }
  }

  var statusMus = extractStatus(stateData)
  if (statusMus === 'failed') {
    finishWithParsed({ status: 'failed', error: extractApiErrorMessage(stateData) || '生成失败' })
    return
  }
  if (statusMus !== 'completed') {
    if (typeof callback === 'function') callback(null, { status: 'processing' })
    scheduleNext()
    return
  }
  var tracksNorm = rowsToMusicTracks(collectMusicTrackCandidates(stateData))
  if (tracksNorm.length) {
    fetchMusicAssetsThenFinish(tracksNorm, finishWithParsed).catch(function () {
      finishWithParsed({ status: 'completed', tracks: tracksNorm })
    })
    return
  }
  var mediaMus = extractMedia(stateData)
  if (mediaMus.url) {
    var singleTracks = [
      {
        audio_url: mediaMus.url,
        image_large_url: '',
        image_url: '',
        title: '',
        prompt: '',
        tags: '',
        duration: 0
      }
    ]
    fetchMusicAssetsThenFinish(singleTracks, finishWithParsed).catch(function () {
      finishWithParsed({
        status: 'completed',
        url: mediaMus.url,
        tracks: singleTracks
      })
    })
    return
  }
  if (mediaMus.base64) {
    var dataUrl = toDataMediaUrl(mediaMus.base64, modelType)
    var singleTracksB64 = [
      {
        audio_url: dataUrl,
        image_large_url: '',
        image_url: '',
        title: '',
        prompt: '',
        tags: '',
        duration: 0
      }
    ]
    finishWithParsed({
      status: 'completed',
      url: dataUrl,
      tracks: singleTracksB64
    })
    return
  }
  finishWithParsed({ status: 'failed', error: extractApiErrorMessage(stateData) || '未返回音频结果' })
}
